import React, { useState, useEffect } from 'react';
import api, { resolveUploadUrl } from '../../config/api';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './AdminPages.css';

const AdminNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    imageUrl: '',
    published: true,
    locale: 'fr'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await api.get('/news/admin/list');
      setNews(response.data.news);
    } catch (error) {
      console.error('Error fetching news:', error);
      alert('Erreur lors du chargement des news');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate content (check if it's not just empty HTML tags)
    const contentText = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!contentText) {
      alert('Le contenu ne peut pas être vide');
      return;
    }
    
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('content', formData.content);
      submitData.append('excerpt', formData.excerpt);
      submitData.append('published', formData.published);
      submitData.append('locale', formData.locale === 'it' ? 'it' : 'fr');
      
      // Add image file if selected
      if (imageFile) {
        submitData.append('image', imageFile);
      } else if (formData.imageUrl && !imageFile) {
        // If editing and no new file, keep existing imageUrl
        submitData.append('imageUrl', formData.imageUrl);
      }

      if (editingNews) {
        await api.put(`/news/${editingNews._id}`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        alert('News mise à jour avec succès');
      } else {
        await api.post('/news', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        alert('News créée avec succès');
      }
      setShowModal(false);
      resetForm();
      fetchNews();
    } catch (error) {
      console.error('Error saving news:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette news?')) return;
    
    try {
      await api.delete(`/news/${id}`);
      alert('News supprimée avec succès');
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      excerpt: newsItem.excerpt,
      imageUrl: newsItem.imageUrl || '',
      published: newsItem.published,
      locale: newsItem.locale === 'it' ? 'it' : 'fr',
    });
    setImageFile(null);
    setImagePreview(newsItem.imageUrl || null);
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      imageUrl: '',
      published: true,
      locale: 'fr',
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingNews(null);
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-page-header">
          <h1>Gestion des News</h1>
          <button 
            className="btn-add"
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <FaPlus /> Ajouter une news
          </button>
        </div>

        {loading ? (
          <div className="loading">Chargement...</div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Type</th>
                  <th>Langue</th>
                  <th>Statut</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {news.map((item) => (
                  <tr key={item._id}>
                    <td>{item.title}</td>
                    <td>
                      <span className={`status ${item.isLocal ? 'published' : 'draft'}`}>
                        {item.isLocal ? 'Local' : 'Externe'}
                      </span>
                    </td>
                    <td>
                      <span className="status published">
                        {item.locale === 'it' ? 'Italien' : 'Français'}
                      </span>
                    </td>
                    <td>
                      <span className={`status ${item.published ? 'published' : 'draft'}`}>
                        {item.published ? 'Publié' : 'Brouillon'}
                      </span>
                    </td>
                    <td>{new Date(item.createdAt).toLocaleDateString('fr-FR')}</td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn-edit"
                        onClick={() => handleEdit(item)}
                        aria-label="Modifier la news"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="btn-delete"
                        onClick={() => handleDelete(item._id)}
                        aria-label="Supprimer la news"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content admin-news-modal" onClick={(e) => e.stopPropagation()}>
              <div className="admin-news-modal-top">
                <h2>{editingNews ? 'Modifier la news' : 'Ajouter une news'}</h2>
                <div className="admin-news-locale-field">
                  <label htmlFor="admin-news-locale">Langue de l&apos;article *</label>
                  <select
                    id="admin-news-locale"
                    name="locale"
                    value={formData.locale === 'it' ? 'it' : 'fr'}
                    onChange={handleChange}
                    required
                    className="admin-news-locale-select"
                  >
                    <option value="fr">Français (site FR)</option>
                    <option value="it">Italiano (sito IT)</option>
                  </select>
                  <p className="admin-news-locale-hint">
                    Visible uniquement pour les visiteurs ayant choisi cette langue (menu du site).
                  </p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="admin-form">
                <div className="form-group">
                  <label>Titre *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Extrait *</label>
                  <textarea
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleChange}
                    required
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Contenu *</label>
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={(value) => setFormData({ ...formData, content: value })}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'align': [] }],
                        ['link'],
                        ['clean']
                      ]
                    }}
                    formats={[
                      'header',
                      'bold', 'italic', 'underline', 'strike',
                      'list', 'bullet',
                      'align',
                      'link'
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label>Image</label>
                  <input
                    type="file"
                    name="image"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={handleImageChange}
                  />
                  {imagePreview && (
                    <div style={{ marginTop: '1rem' }}>
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px', 
                          borderRadius: '4px',
                          border: '1px solid #ddd'
                        }} 
                      />
                    </div>
                  )}
                  {!imagePreview && formData.imageUrl && (
                    <div style={{ marginTop: '1rem' }}>
                      <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                        Image actuelle:
                      </p>
                      <img 
                        src={formData.imageUrl.startsWith('http') ? formData.imageUrl : resolveUploadUrl(formData.imageUrl)} 
                        alt="Current" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px', 
                          borderRadius: '4px',
                          border: '1px solid #ddd'
                        }} 
                      />
                    </div>
                  )}
                </div>

                <div className="form-group checkbox">
                  <label>
                    <input
                      type="checkbox"
                      name="published"
                      checked={formData.published}
                      onChange={handleChange}
                    />
                    Publié
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-submit">
                    {editingNews ? 'Mettre à jour' : 'Créer'}
                  </button>
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNews;


